import { useState, useEffect, useCallback } from "react";

import { Room } from "twilio-video";

import { useDeepgram } from "./useDeepgram";

export default function useDeepgramTranscription(room: Room | null, isTranscribing: boolean) {
	const [transcripts, setTranscripts] = useState<string[]>([]);
	const { startTranscription, stopTranscription } = useDeepgram();

	const startTranscriptionWithParticipant = useCallback(() => {
		if (!room) return;

		const remoteParticipant = Array.from(room.participants.values())[0];
		if (!remoteParticipant) {
			return;
		}

		const remoteAudioTrack = Array.from(remoteParticipant.audioTracks.values())[0]?.track;
		if (!remoteAudioTrack) {
			return;
		}

		const REALTIME_DISPLAY_ID = 'realtime-display';
		startTranscription(
			REALTIME_DISPLAY_ID,
			remoteAudioTrack,
			'Student', // Speaker label not used for display, but required
			(transcriptData) => {
				setTranscripts((prev) => {
					if (prev.length > 0) {
						return [...prev.slice(0, -1), transcriptData.text];
					}
					return [transcriptData.text];
				});
			},
			false
		);
	}, [room, startTranscription]);

	useEffect(() => {
		if (!room) return;

		const handleParticipantConnected = () => {
			if (isTranscribing) {
				startTranscriptionWithParticipant();
			}
		};

		const handleTrackSubscribed = () => {
			if (isTranscribing) {
				startTranscriptionWithParticipant();
			}
		};

		room.on("participantConnected", handleParticipantConnected);
		room.on("trackSubscribed", handleTrackSubscribed);

		return () => {
			room.off("participantConnected", handleParticipantConnected);
			room.off("trackSubscribed", handleTrackSubscribed);
		};
	}, [room, isTranscribing, startTranscriptionWithParticipant]);

	useEffect(() => {
		if (isTranscribing) {
			startTranscriptionWithParticipant();
		} else {
			stopTranscription('realtime-display');
			setTranscripts([]);
		}
	}, [isTranscribing, startTranscriptionWithParticipant, stopTranscription]);

	return {
		transcripts,
	};
}
